/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {Inject, Injectable, Optional} from '@angular/core';


import {Location} from './location';
import {APP_BASE_HREF, LocationStrategy} from './location_strategy';
import {LocationChangeListener, PlatformLocation} from './platform_location';
/**
 * \@whatItDoes Use URL for storing application location data.
 * \@description 
 * `PathLocationStrategy` is a {\@link LocationStrategy} used to configure the
 * {\@link Location} service to represent its state in the
 * [path](https://en.wikipedia.org/wiki/Uniform_Resource_Locator#Syntax) of the
 * browser's URL.
 * 
 * If you're using `PathLocationStrategy`, you must provide a {\@link APP_BASE_HREF}
 * or add a base element to the document. This URL prefix that will be preserved
 * when generating and recognizing URLs.
 * 
 * For instance, if you provide an `APP_BASE_HREF` of `'/my/app'` and call
 * `location.go('/foo')`, the browser's URL will become
 * `example.com/my/app/foo`.
 * 
 * Similarly, if you add `<base href='/my/app'/>` to the document and call
 * `location.go('/foo')`, the browser's URL will become
 * `example.com/my/app/foo`.
 * 
 * ### Example
 * 
 * {\@example common/location/ts/path_location_component.ts region='LocationComponent'}
 * 
 * \@stable
 */
export class PathLocationStrategy extends LocationStrategy {
private _baseHref: string;
/**
 * @param {?} _platformLocation
 * @param {?=} href
 */
constructor(
private _platformLocation: PlatformLocation,
        href?: string) {
    super();

    if (href == null) {
      href = this._platformLocation.getBaseHrefFromDOM();
    }

    if (href == null) {
      throw new Error(
          `No base href set. Please provide a value for the APP_BASE_HREF token or add a base element to the document.`);
    }

    this._baseHref = href;
  }
/**
 * @param {?} fn
 * @return {?}
 */
onPopState(fn: LocationChangeListener): void {
    this._platformLocation.onPopState(fn);
    this._platformLocation.onHashChange(fn);
  }
/**
 * @return {?}
 */
getBaseHref(): string { return this._baseHref; }
/**
 * @param {?} internal
 * @return {?}
 */
prepareExternalUrl(internal: string): string {
    return Location.joinWithSlash(this._baseHref, internal);
  }
/**
 * @param {?=} includeHash
 * @return {?}
 */
path(includeHash: boolean = false): string {
    const /** @type {?} */ pathname = this._platformLocation.pathname +
        Location.normalizeQueryParams(this._platformLocation.search);
    const /** @type {?} */ hash = this._platformLocation.hash;
    return hash && includeHash ? `${pathname}${hash}` : pathname;
  }
/**
 * @param {?} state
 * @param {?} title
 * @param {?} url
 * @param {?} queryParams
 * @return {?}
 */
pushState(state: any, title: string, url: string, queryParams: string) {
    const /** @type {?} */ externalUrl = this.prepareExternalUrl(url + Location.normalizeQueryParams(queryParams));
    this._platformLocation.pushState(state, title, externalUrl);
  }
/**
 * @param {?} state
 * @param {?} title
 * @param {?} url
 * @param {?} queryParams
 * @return {?}
 */
replaceState(state: any, title: string, url: string, queryParams: string) {
    const /** @type {?} */ externalUrl = this.prepareExternalUrl(url + Location.normalizeQueryParams(queryParams));
    this._platformLocation.replaceState(state, title, externalUrl);
  }
/**
 * @return {?}
 */
forward(): void { this._platformLocation.forward(); }
/**
 * @return {?}
 */
back(): void { this._platformLocation.back(); }
static decorators: DecoratorInvocation[] = [
{ type: Injectable },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: PlatformLocation, },
{type: undefined, decorators: [{ type: Optional }, { type: Inject, args: [APP_BASE_HREF, ] }, ]},
];
}

function PathLocationStrategy_tsickle_Closure_declarations() {
/** @type {?} */
PathLocationStrategy.decorators;
/**
 * @nocollapse
 * @type {?}
 */
PathLocationStrategy.ctorParameters;
/** @type {?} */
PathLocationStrategy.prototype._baseHref;
/** @type {?} */
PathLocationStrategy.prototype._platformLocation;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
